import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "@/components/ui/accordion";
import { BookOpen, Search, AlertTriangle } from "lucide-react";

export default function CatalogPage() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/catalog").then(r => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    items
      .filter(i => {
        if (!search) return true;
        const q = search.toLowerCase();
        if (i.name.toLowerCase().includes(q)) return true;
        const aliases = i.aliases || [];
        return aliases.some((a) => a.toLowerCase().includes(q));
      })
      .forEach(i => {
        map[i.category] = map[i.category] || [];
        map[i.category].push(i);
      });
    return map;
  }, [items, search]);

  const categories = Object.keys(grouped).sort();

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-blue-600 text-white grid place-items-center shadow">
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Catálogo de referencia</h1>
            <p className="text-slate-600 text-base mt-1">Medicamentos comunes en México con un rango de precio orientativo de mercado.</p>
          </div>
        </div>

        <Card className="p-4 rounded-2xl border border-slate-200 bg-white mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar en el catálogo…" className="h-12 pl-9 bg-slate-50" data-testid="catalog-search-input" />
          </div>
        </Card>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex gap-3" data-testid="catalog-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 leading-relaxed">
            <strong>Importante:</strong> Los precios son orientativos del mercado mexicano y pueden variar.
            Ciudad Enfermería no almacena ni vende medicamentos. Verifica siempre la caducidad y el empaque.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-12">Cargando catálogo…</div>
        ) : (
          <Accordion type="multiple" defaultValue={categories.slice(0, 2)} className="space-y-3">
            {categories.map((cat) => (
              <AccordionItem key={cat} value={cat} className="bg-white rounded-2xl border border-slate-200 px-4 overflow-hidden">
                <AccordionTrigger className="hover:no-underline py-4" data-testid={`catalog-category-${cat}`}>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100">{grouped[cat].length}</Badge>
                    <span className="font-display font-semibold text-base text-slate-900">{cat}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid sm:grid-cols-2 gap-3 pb-3">
                    {grouped[cat].map((m) => (
                      <div key={m.name} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`catalog-item-${m.name}`}>
                        <div className="font-medium text-slate-900 text-sm">{m.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{m.common_presentation}</div>
                        {(m.aliases || []).length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            <span className="text-[10px] text-slate-500 mr-0.5">también:</span>
                            {(m.aliases || []).map((a) => (
                              <span key={a} className="px-1.5 py-0.5 rounded-md bg-white text-slate-700 text-[10px] font-medium border border-slate-200">{a}</span>
                            ))}
                          </div>
                        )}
                        <div className="mt-2 inline-flex items-center px-2 py-1 rounded-md bg-white border border-blue-100 text-blue-800 text-xs font-semibold">
                          ${m.price_min} – ${m.price_max} MXN
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </div>
  );
}
